// TODO(dropdown-panel-reposition): temporary. Delete this file — and the
// `PanelHost: { Panel: orgSwitcherPanelPin }` override on the shell's
// DropdownRoot in app.js — once @symbo.ls/dropdown re-anchors on resize,
// re-measures a Panel whose content grows (followContent, at the foot of
// this file) and accepts an `inset` option (openOrgSwitcherMenu then passes
// `inset: { right: 'A1' }` itself).
//
// The org switcher's dropdown Panel pins to the viewport's right edge (the
// `A1` spacing step) instead of the plugin's anchor-computed `left`. The
// @symbo.ls/dropdown Panel writes `left` from the trigger rect it snapshotted
// at open — through its reactive `left` prop, inline in its `onRender`, and
// inline in a rAF after the content mounts — so a window resize left the menu
// stranded at the old x. A right-anchored Panel follows the viewport instead.
//
// Every other dropdown keeps the plugin's anchor positioning: each override
// below runs the plugin's own hook first, and only the org switcher's layer
// is re-pinned. The Panel node is REUSED across layers, so every other layer
// gets the inline `right` this file wrote taken back off.

// Import createEffect through the `smbls` umbrella, NOT '@symbo.ls/signal' —
// the same signal-copy rule DropdownRoot.js documents.
import { createEffect } from 'smbls'
import { DropdownRoot } from '@symbo.ls/dropdown'
import { ORG_SWITCHER_DROPDOWN_ID } from '@symbo-ls/workspace-shared/functions/orgSwitcherDropdown.js'

const RIGHT_INSET = 'var(--spacing-A1)'

// The plugin hooks this file wraps. Checked at import so a plugin release that
// renames or drops one fails here, by name, instead of on the first dropdown.
const _plugin = DropdownRoot?.PanelHost?.Panel
for (const hook of ['left', 'onInit', 'onRender']) {
  if (typeof _plugin?.[hook] !== 'function') {
    throw new Error(
      `[orgSwitcherPanelPin] @symbo.ls/dropdown DropdownRoot.PanelHost.Panel.${hook} is not a function — ` +
        'the plugin changed shape; update or remove this override.'
    )
  }
}

const _isOrgSwitcher = (layer) => layer?.id === ORG_SWITCHER_DROPDOWN_ID

// A navbar RAIL (shared functions/navbarLayout.js) puts the switcher at the
// foot of a left or right column, and its menu opens beside the rail
// (AppOrgSwitcher.menuPlacement): the plugin's own anchor places it there, as
// it does every other dropdown. Pinned to the viewport's right edge instead,
// it opened across the screen from a left rail and over a right one
// (measured on dev 50e520dd0). A shell without the layout read pins as ever.
const _onRail = (el) => {
  const fns = el?.context?.functions
  if (!fns || typeof fns.navbarLayoutRead !== 'function') return false
  return !!(el.call('navbarLayoutRead', el.state?.root) || {}).rail
}
const _pinsRight = (el, layer) => _isOrgSwitcher(layer) && !_onRail(el)

// `layer` is passed in where the caller already holds it: inside a rAF the
// reactive `el.parent.layer` getter is not guaranteed to resolve (see the
// `_applyChrome` note in DropdownRoot.js).
export const pinPanel = (el, layer) => {
  if (!el.node) return
  if (_pinsRight(el, layer)) {
    el.node.style.removeProperty('left')
    el.node.style.right = RIGHT_INSET
  } else {
    el.node.style.removeProperty('right')
  }
}

export const orgSwitcherPanelPin = {
  left: (el) => (_pinsRight(el, el.parent?.layer) ? 'auto' : _plugin.left(el)),

  onInit: (el) => {
    _plugin.onInit(el)
    // On every layer change: settle `right` now, then again after the
    // plugin's content-mount rAF has written its inline `left` (a double rAF
    // lands after that single one).
    // `__pinnedLayer` drops a frame callback that a newer layer has
    // overtaken, so a stale org-switcher pin never lands on the next menu.
    const effect = createEffect(() => {
      const layer = el.parent?.layer
      el.__pinnedLayer = layer
      pinPanel(el, layer)
      if (!layer || typeof requestAnimationFrame === 'undefined') return
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (el.__pinnedLayer === layer) pinPanel(el, layer)
        })
      )
    })
    if (el.__ref) el.__ref.__effects?.push(effect)
  },

  onRender: (el) => {
    _plugin.onRender(el)
    pinPanel(el, el.parent?.layer)
    followContent(el)
  }
}

// THE PANEL FOLLOWS ITS CONTENT (same TODO as the head of this file: delete
// once @symbo.ls/dropdown re-measures by itself). The plugin measures and
// places the Panel once, a frame after the layer's content mounts; a body
// that grows after that (the invite's member list, loaded by the body
// itself) grows from where it was placed — past the screen's edge for a
// menu that opens upward or beside a navbar rail (measured on dev
// 1055707e3: the invite beside a left rail ended 14px below the screen).
// One observer on the reused Panel node: a size change runs the plugin's
// own measure-and-place again (left, top, the fitted max height), then the
// pin. A layer-less (closed) Panel is left alone; an equal size does
// nothing, so the place it writes cannot loop.
const followContent = (el) => {
  if (el.__panelFollow || !el.node || typeof ResizeObserver === 'undefined') return
  let last = null
  el.__panelFollow = new ResizeObserver(() => {
    const node = el.node
    if (!node || !el.parent?.layer) {
      last = null
      return
    }
    const r = node.getBoundingClientRect()
    const size = { w: Math.round(r.width), h: Math.round(r.height) }
    if (last && Math.abs(last.w - size.w) <= 1 && Math.abs(last.h - size.h) <= 1) return
    last = size
    _plugin.onRender(el)
    pinPanel(el, el.parent?.layer)
  })
  el.__panelFollow.observe(el.node)
}
